import React, { useState } from 'react'

import { formatResourceFilterOptions } from 'utils/functions'
import { useTranslation } from 'language/translator'

/* Atoms */
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import Switch from 'components_new/atoms/Switch'

import Text from 'components_new/atoms/Text'

/* Molecules */
import FormSection from 'components_new/molecules/FormSection'
import PreviewEmbeddedContent from 'components_new/molecules/PreviewEmbeddedContent'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SidebarDialog from 'components_new/molecules/SidebarDialog'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

/* Organisms */
import ApiDetails from './ApiDetails'
import PreviewWidgets from './PreviewWidgets'
import Tags from './Tags'

/* Actions */
import { useDashboardActions } from 'redux/actions/Dashboards'

/* Hooks */
import { useDashboard } from 'redux/hooks/Dashboards'
import { useGlobalFilterSaveKey } from 'redux/hooks/GlobalFilter'
import { useAllowEmbedDashboard } from 'redux/hooks/Permissions'

/* Types */
import { TResourceFilter } from 'types/GlobalDashboardGroup'
import {
  useResourceFilterData,
  useResourceFilterOptions
} from 'redux/hooks/ResourceFilter'

interface EmbedDashboardDialogProps {
  id: string | null
  dashboardGroupFilters: TResourceFilter[]
  onClose: () => void
  open: boolean
}

const EmbedDashboardDialog = (props: EmbedDashboardDialogProps) => {
  const { id, dashboardGroupFilters, onClose, open } = props

  const resourceFilterOptions = useResourceFilterOptions()
  const resourceFilterData = useResourceFilterData()

  const handleClose = () => {
    onClose()
  }

  const { t } = useTranslation()
  const { tryUpdateDashboard } = useDashboardActions()

  const dashboard = useDashboard(id)
  const globalFilterSaveKey = useGlobalFilterSaveKey()
  const allowEmbedDashboard = useAllowEmbedDashboard()

  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean
    handleConfirm: () => void
      }>({
        open: false,
        handleConfirm: () => {}
      })

  if (!dashboard || !allowEmbedDashboard) return null

  const apiAccess = dashboard.share_settings?.api_access ?? false
  const apiAccessLink = dashboard.share_settings?.api_access_link ?? ''
  const isPublic = dashboard.share_settings?.is_public ?? false
  const publicLink = dashboard.share_settings?.public_link ?? ''

  const dashboardFilterOptions = formatResourceFilterOptions(
    dashboardGroupFilters,
    resourceFilterOptions,
    resourceFilterData,
    globalFilterSaveKey
  )

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialog({ handleConfirm: () => {}, open: false })
  }

  const setApiAccess = (value: boolean) => {
    if (isPublic) {
      setConfirmationDialog({
        handleConfirm: () =>
          tryUpdateDashboard(dashboard.id, {
            data: { api_access: value, is_public: false }
          }),
        open: true
      })
    } else {
      tryUpdateDashboard(dashboard.id, {
        data: { api_access: value, is_public: false }
      })
    }
  }

  const setIsPublic = (value: boolean) => {
    if (apiAccess) {
      setConfirmationDialog({
        handleConfirm: () =>
          tryUpdateDashboard(dashboard.id, {
            data: { is_public: value, api_access: false }
          }),
        open: true
      })
    } else {
      tryUpdateDashboard(dashboard.id, {
        data: { is_public: value, api_access: false }
      })
    }
  }

  const component = t('dashboards', 'tab', true)

  return (
    <SidebarDialog
      actions={
        <Button onClick={handleClose} variant="text">
          {t('global', 'close')}
        </Button>
      }
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 'md'
        }
      }}
      title={t('global', 'embed') + ' ' + dashboard.title}
    >
      <Box>
        <FormSection
          helperText={t(
            'dashboards',
            'enable_external_access_helper_text',
            false,
            { component }
          )}
          title={t('dashboards', 'enable_external_access')}
        >
          <SettingsGroup>
            <SettingsItem
              actions={
                <Switch
                  color="success"
                  checked={isPublic}
                  onChange={() => setIsPublic(!isPublic)}
                  size="medium"
                />
              }
              body={t('dashboards', 'public_link_description')}
              icon={<Icon name={'PublicOutlined'} />}
              title={t('dashboards', 'public_link')}
            />
            <SettingsItem
              actions={
                <Switch
                  color="success"
                  checked={apiAccess}
                  onChange={() => setApiAccess(!apiAccess)}
                  size="medium"
                />
              }
              body={t('dashboards', 'protected_link_description')}
              icon={<Icon name={'VpnLock'} />}
              title={t('dashboards', 'protected_link')}
            />
          </SettingsGroup>
        </FormSection>
        {isPublic || apiAccess ? (
          <Box sx={{ flex: 1 }}>
            {apiAccess ? <ApiDetails /> : null}
            <FormSection
              helperText={t('dashboards', 'embed_link_helper_text', false, {
                component
              })}
              title={t('dashboards', 'embed_link_title')}
            >
              <PreviewEmbeddedContent
                availableFeatures={
                  apiAccess
                    ? [
                        'responsive_mode',
                        'user_filter',
                        'image_export',
                        'hide_footer'
                      ]
                    : ['responsive_mode', 'filter', 'auto_refresh']
                }
                filterOptions={dashboardFilterOptions}
                link={
                  apiAccess ? apiAccessLink + '?token=YOUR_TOKEN' : publicLink
                }
                title={dashboard.title}
                helperText={
                  apiAccess ? t('dashboards', 'token_instruction') : undefined
                }
                showPreview={!apiAccess}
                sx={{ mb: 4 }}
              />
            </FormSection>
            <Collapse in={apiAccess}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1
                }}
              >
                <PreviewWidgets dashboard={dashboard} />
                <Tags dashboard={dashboard} />
              </Box>
            </Collapse>
          </Box>
        ) : null}
      </Box>
      <SimpleDialog
        title={t('dashboards', 'confirm_change_access_type_title')}
        open={confirmationDialog.open}
        onClose={handleCloseConfirmationDialog}
        actions={
          <>
            <Button variant="text" onClick={handleCloseConfirmationDialog}>
              {t('global', 'cancel')}
            </Button>
            <Button
              onClick={() => {
                confirmationDialog.handleConfirm()
                handleCloseConfirmationDialog()
              }}
              variant="contained"
            >
              {t('global', 'continue')}
            </Button>
          </>
        }
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '512px!important'
          }
        }}
      >
        <Text>{t('dashboards', 'confirm_change_access_type_body')}</Text>
      </SimpleDialog>
    </SidebarDialog>
  )
}

export default EmbedDashboardDialog
